import React, { useContext, useEffect, useState } from 'react'
import log from 'loglevel'
import { useLocation, useNavigate } from 'react-router-dom'
import AuthContext from 'src/context/authContext'
import { GatekeeperFlag, IsGatekeeperEnabled } from 'src/context/Gatekeeper'
import { useCognitoContext } from 'src/context/FormContext/CognitoContext'
import { NTRCE_API_URL } from 'src/adapter/gRpcAdapter'
import { AccountPromiseClient } from '@trustero/trustero-api-web/lib/account/account_grpc_web_pb'
import { SendSignInEmailRequest } from '@trustero/trustero-api-web/lib/account/account_pb'
import {
  StandardButton,
  StandardButtonSize,
  StandardButtonVariant,
} from 'src/components/Reusable/Buttons'
import { DirectRoutes } from 'src/components/Reusable/RootPage/RootPage.constants'
import {
  CognitoAuthGrant,
  getCognitoAuthGrant,
  msRedirect,
} from 'src/Utils/oidc'
import { getLoginRedirectFromSearch } from 'src/Utils/loginRedirect'
import { ReactComponent as KeyIcon } from 'src/components/Icons/Basic/icons_key.svg'
import { ReactComponent as GoogleSVG } from 'src/components/Icons/Dependencies/google/google.svg'
import { ReactComponent as OktaSVG } from 'src/components/Icons/Dependencies/okta/okta.svg'
import { ReactComponent as PingOneSVG } from 'src/components/Icons/Dependencies/pingone/pingone.svg'
import { ReactComponent as OneLoginSVG } from 'src/components/Icons/Dependencies/onelogin/oneLogin.svg'
import { ReactComponent as MicrosoftSVG } from 'src/components/Icons/Dependencies/microsoft/microsoft.svg'
import { ReactComponent as EntraIdSVG } from 'src/components/Icons/Dependencies/microsoft/entra.svg'
import { ReactComponent as Auth0SVG } from 'src/components/Icons/Dependencies/auth0/auth0.svg'
import { Spinner } from 'src/Throbber/Spinner'
import { FlexAlign, FlexRow } from 'src/components/Reusable/Flex'
import {
  LoginProviders,
  clearLastLogin,
  getLastLogin,
  setLastLogin,
} from './LoginPage.constants'
import { AttemptLogin } from './AttemptLogin'
import { RedirectToHubspot } from './Login.components'
import {
  NewLoginPageContainer,
  BrandSection,
  BrandLogoContainer,
  BrandLogo,
  LoginCardContainer,
  LoginCard,
  LoginCardHeader,
  LoginButtonsGrid,
  NewLoginButton,
  NewLoginIconWrapper,
  DividerRow,
  AccessLinkButton,
  TrusteroLogoBig,
  AILogo,
  EmailInputContainer,
  StyledEmailInput,
  EmailActionButtons,
  AdditionalSignInButton,
} from './LoginPage.styles'

export enum FormStage {
  START,
  INPUT,
  ERROR,
  SUCCESS,
}

export const LoginPageNew = ({
  originalEmail,
  originalURL = '',
}: {
  originalEmail?: string
  originalURL?: string
}): JSX.Element => {
  const { authCtx } = useContext(AuthContext)
  const location = useLocation()
  const navigate = useNavigate()
  const { setInitialCognitoAuth } = useCognitoContext()
  const [email, setEmail] = useState(originalEmail || authCtx?.email || '')
  const [lastLogin, setLastLoginState] = useState<LoginProviders | null>(
    getLastLogin(),
  )
  const [formStage, setFormStage] = useState<FormStage>(() => {
    return getLastLogin() === LoginProviders.ACCESS_LINK
      ? FormStage.INPUT
      : FormStage.START
  })
  const [isSending, setIsSending] = useState(false)
  const AccountClient = new AccountPromiseClient(NTRCE_API_URL)

  const [isRedirecting, setIsRedirecting] = useState(false)
  const [isPendingAuth, setIsPendingAuth] = useState(() => {
    const pending = sessionStorage.getItem('pendingOAuth') === 'true'
    sessionStorage.removeItem('pendingOAuth')
    return pending
  })

  useEffect(() => {
    if (!isPendingAuth) return
    const timeout = setTimeout(() => setIsPendingAuth(false), 3000)
    return () => clearTimeout(timeout)
  }, [isPendingAuth])

  const isMsLoginEnabled = IsGatekeeperEnabled(GatekeeperFlag.MS_LOGIN)
  const isPingOneEnabled = IsGatekeeperEnabled(GatekeeperFlag.PINGONE_LOGIN)

  const LOGIN_PROVIDERS_LIST = React.useMemo(() => {
    const providers = [
      {
        id: LoginProviders.GOOGLE,
        name: 'Google',
        icon: <GoogleSVG width="32" height="32" />,
        onClick: () => {
          getCognitoAuthGrant({ provider: LoginProviders.GOOGLE })
        },
      },
    ]

    if (isMsLoginEnabled) {
      providers.push(
        {
          id: LoginProviders.MICROSOFT,
          name: 'Microsoft',
          icon: <MicrosoftSVG width="32" height="32" />,
          onClick: () => {
            msRedirect(false)
          },
        },
        {
          id: LoginProviders.ENTRA,
          name: 'Microsoft Entra ID',
          icon: <EntraIdSVG width="32" height="32" />,
          onClick: () => {
            msRedirect(true)
          },
        },
      )
    } else {
      providers.push({
        id: LoginProviders.MICROSOFT,
        name: 'Microsoft (via Auth0)',
        icon: <MicrosoftSVG width="32" height="32" />,
        onClick: () => {
          getCognitoAuthGrant({ provider: LoginProviders.OKTA })
        },
      })
    }

    providers.push(
      {
        id: LoginProviders.AUTH0,
        name: 'Auth0',
        icon: <Auth0SVG width="32" height="32" />,
        onClick: () => {
          getCognitoAuthGrant({ provider: LoginProviders.OKTA })
        },
      },
      {
        id: LoginProviders.OKTA,
        name: 'Okta',
        icon: <OktaSVG width="32" height="32" />,
        onClick: () => {
          getCognitoAuthGrant({ provider: LoginProviders.OKTA })
        },
      },
      {
        id: LoginProviders.ONELOGIN,
        name: 'OneLogin',
        icon: <OneLoginSVG width="32" height="32" />,
        onClick: () => {
          getCognitoAuthGrant({ provider: LoginProviders.ONELOGIN })
        },
      },
    )

    if (isPingOneEnabled) {
      providers.push({
        id: LoginProviders.PINGONE,
        name: 'PingOne',
        icon: <PingOneSVG width="32" height="32" />,
        onClick: () => {
          setInitialCognitoAuth({
            provider: LoginProviders.PINGONE,
          } as CognitoAuthGrant)
          navigate(`/${DirectRoutes.OIDC_EMAIL}${location.search}`, {
            state: { from: location },
          })
        },
      })
    }

    providers.push({
      id: LoginProviders.ENTRAIDSSO,
      name: 'Enterprise SSO',
      icon: (
        <NewLoginIconWrapper>
          <strong style={{ fontSize: '1.25rem' }}>SSO</strong>
        </NewLoginIconWrapper>
      ),
      onClick: () => {
        setInitialCognitoAuth({
          provider: LoginProviders.ENTRAIDSSO,
        } as CognitoAuthGrant)
        navigate(`/${DirectRoutes.OIDC_EMAIL}${location.search}`, {
          state: { from: location },
        })
      },
    })

    return providers
  }, [
    isMsLoginEnabled,
    isPingOneEnabled,
    location,
    navigate,
    setInitialCognitoAuth,
  ])

  const handleProviderClick = (provider: (typeof LOGIN_PROVIDERS_LIST)[0]) => {
    setLastLogin(provider.id)
    sessionStorage.setItem('pendingOAuth', 'true')
    setIsPendingAuth(true)
    provider.onClick()
  }

  const handleClearLastLogin = () => {
    clearLastLogin()
    setLastLoginState(null)
    setFormStage(FormStage.START)
  }

  const filteredProviders = lastLogin
    ? LOGIN_PROVIDERS_LIST.filter((p) => p.id === lastLogin)
    : LOGIN_PROVIDERS_LIST

  const sendLoginEmail = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (formStage !== FormStage.INPUT) return

    setIsSending(true)
    try {
      const redirectUrl =
        getLoginRedirectFromSearch(window.location.search) || originalURL

      const request = new SendSignInEmailRequest()
        .setEmail(email)
        .setRedirectUrl(redirectUrl)

      await AccountClient.sendSignInEmail(request)
      setLastLogin(LoginProviders.ACCESS_LINK)
      setLastLoginState(LoginProviders.ACCESS_LINK)
      setEmail('')
      setFormStage(FormStage.SUCCESS)
    } catch (e) {
      log.error('Error in EmailLoginNew', e)
      setFormStage(FormStage.ERROR)
    } finally {
      setIsSending(false)
    }
  }

  return (
    <NewLoginPageContainer>
      <AttemptLogin setIsRedirecting={setIsRedirecting} />
      <BrandSection>
        <BrandLogoContainer>
          <BrandLogo>
            <TrusteroLogoBig /> <AILogo>AI</AILogo>
          </BrandLogo>
          <span style={{ marginLeft: '20px' }}>trustero.com</span>
        </BrandLogoContainer>
      </BrandSection>
      {isRedirecting ? (
        <LoginCardContainer>
          <LoginCard>
            <RedirectToHubspot />
          </LoginCard>
        </LoginCardContainer>
      ) : isPendingAuth ? (
        <LoginCardContainer>
          <LoginCard>
            <FlexRow
              justify={FlexAlign.CENTER}
              align={FlexAlign.CENTER}
              gap={10}
            >
              <Spinner color="primary" size="m" />
              <span>Authenticating...</span>
            </FlexRow>
          </LoginCard>
        </LoginCardContainer>
      ) : (
        <LoginCardContainer>
          <LoginCard>
            {lastLogin !== LoginProviders.ACCESS_LINK && (
              <>
                <LoginCardHeader>
                  {lastLogin ? 'Sign in again with' : 'Sign in with'}
                </LoginCardHeader>
                <LoginButtonsGrid columns={lastLogin ? 1 : 2}>
                  {filteredProviders.map((provider) => (
                    <NewLoginButton
                      key={provider.id}
                      onClick={() => handleProviderClick(provider)}
                    >
                      {provider.id === LoginProviders.ENTRAIDSSO ? (
                        provider.icon
                      ) : (
                        <NewLoginIconWrapper>
                          {provider.icon}
                        </NewLoginIconWrapper>
                      )}
                      <span>{provider.name}</span>
                    </NewLoginButton>
                  ))}
                </LoginButtonsGrid>
              </>
            )}
            {lastLogin && (
              <>
                {lastLogin !== LoginProviders.ACCESS_LINK && (
                  <DividerRow>
                    <span>OR</span>
                  </DividerRow>
                )}
                {lastLogin === LoginProviders.ACCESS_LINK ? (
                  <>
                    <LoginCardHeader>Sign in with Access Link</LoginCardHeader>
                    <EmailInputContainer onSubmit={sendLoginEmail}>
                      {formStage === FormStage.INPUT ? (
                        <>
                          <StyledEmailInput
                            type="text"
                            placeholder="Enter Email Address"
                            value={email}
                            autoComplete="email"
                            onChange={(e) => setEmail(e.target.value)}
                            autoFocus
                          />
                          <EmailActionButtons>
                            <StandardButton
                              type="button"
                              variant={StandardButtonVariant.SECONDARY}
                              buttonSize={StandardButtonSize.LARGE}
                              onClick={handleClearLastLogin}
                              style={{ borderRadius: '12px', flex: 1 }}
                              disabled={isSending}
                            >
                              Clear Selection
                            </StandardButton>
                            <StandardButton
                              variant={StandardButtonVariant.PRIMARY}
                              buttonSize={StandardButtonSize.LARGE}
                              type="submit"
                              style={{ borderRadius: '12px', flex: 1 }}
                              disabled={isSending}
                            >
                              {isSending ? (
                                <Spinner size="s" color="busy" />
                              ) : (
                                'Send'
                              )}
                            </StandardButton>
                          </EmailActionButtons>
                        </>
                      ) : (
                        <div style={{ textAlign: 'center', padding: '1rem' }}>
                          <p
                            style={{
                              marginBottom: '1rem',
                              color:
                                formStage === FormStage.SUCCESS
                                  ? 'green'
                                  : 'red',
                            }}
                          >
                            {formStage === FormStage.SUCCESS
                              ? 'Check Email for Access Link'
                              : 'An error occurred. Please try again.'}
                          </p>
                          <StandardButton
                            type="button"
                            variant={StandardButtonVariant.SECONDARY}
                            buttonSize={StandardButtonSize.LARGE}
                            onClick={() => {
                              setEmail('')
                              setFormStage(FormStage.INPUT)
                            }}
                            style={{ borderRadius: '12px', width: '100%' }}
                          >
                            Okay
                          </StandardButton>
                        </div>
                      )}
                    </EmailInputContainer>
                    <DividerRow>
                      <span>OR</span>
                    </DividerRow>
                    <AdditionalSignInButton onClick={handleClearLastLogin}>
                      Additional Sign-in Options
                    </AdditionalSignInButton>
                  </>
                ) : (
                  <AdditionalSignInButton onClick={handleClearLastLogin}>
                    Additional Sign-in Options
                  </AdditionalSignInButton>
                )}
              </>
            )}
            {!lastLogin && (
              <>
                <DividerRow>
                  <span>OR</span>
                </DividerRow>
                {formStage === FormStage.START ? (
                  <AccessLinkButton
                    onClick={() => setFormStage(FormStage.INPUT)}
                  >
                    <KeyIcon width="24" height="24" />
                    Send me an Access Link
                  </AccessLinkButton>
                ) : (
                  <EmailInputContainer onSubmit={sendLoginEmail}>
                    {formStage === FormStage.INPUT ? (
                      <>
                        <StyledEmailInput
                          type="text"
                          placeholder="Enter Email Address"
                          value={email}
                          autoComplete="email"
                          onChange={(e) => setEmail(e.target.value)}
                          autoFocus
                        />
                        <EmailActionButtons>
                          <StandardButton
                            type="button"
                            variant={StandardButtonVariant.SECONDARY}
                            buttonSize={StandardButtonSize.LARGE}
                            onClick={() => setFormStage(FormStage.START)}
                            style={{ borderRadius: '12px', flex: 1 }}
                            disabled={isSending}
                          >
                            Back
                          </StandardButton>
                          <StandardButton
                            variant={StandardButtonVariant.PRIMARY}
                            buttonSize={StandardButtonSize.LARGE}
                            type="submit"
                            style={{ borderRadius: '12px', flex: 1 }}
                            disabled={isSending}
                          >
                            {isSending ? (
                              <Spinner size="s" color="busy" />
                            ) : (
                              'Send'
                            )}
                          </StandardButton>
                        </EmailActionButtons>
                      </>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '1rem' }}>
                        <p
                          style={{
                            marginBottom: '1rem',
                            color:
                              formStage === FormStage.SUCCESS ? 'green' : 'red',
                          }}
                        >
                          {formStage === FormStage.SUCCESS
                            ? 'Check Email for Access Link'
                            : 'An error occurred. Please try again.'}
                        </p>
                        <StandardButton
                          type="button"
                          variant={StandardButtonVariant.SECONDARY}
                          buttonSize={StandardButtonSize.LARGE}
                          onClick={() => {
                            setEmail('')
                            setFormStage(FormStage.START)
                          }}
                          style={{ borderRadius: '12px', width: '100%' }}
                        >
                          Okay
                        </StandardButton>
                      </div>
                    )}
                  </EmailInputContainer>
                )}
              </>
            )}
          </LoginCard>
        </LoginCardContainer>
      )}
    </NewLoginPageContainer>
  )
}
